<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/finance'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    v-if="loaded"
  />
</template>

<script>
import SplitViewTemplate from "../../../templates/split-view";
import RouteMixin from "@/_mixins/route.mixin";
import { GLChartService } from "../../../_services/api/gl/gl-chart.service";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  computed: {
    // Derives "1", "2" ... "10" from the route e.g. /gl-reporting-group-03/...
    groupNumber() {
      const match = this.$route.path.match(/gl-reporting-group-(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    },
    // Zero-padded: "01", "02" ... "10"
    groupNumberPadded() {
      return String(this.groupNumber).padStart(2, "0");
    },
    groupLabel() {
      return `GL Reporting Group ${this.groupNumber}`;
    },
    groupEndpoint() {
      return `GLGroup${this.groupNumber}`;
    },
    groupRoute() {
      return `/gl-reporting-group-${this.groupNumberPadded}`;
    },

    setActiveReadOnly() {
      return this.formModel["SetDefault"] ? true : false;
    },
    codeMaxLength() {
      const lengthMap = { 1: 2, 2: 3 };
      return lengthMap[this.groupNumber] || 5;
    },
    codeIsNumeric() {
      return this.groupNumber > 2;
    },
    data() {
      return {
        title: this.groupLabel,
        pageRoute: this.groupRoute,
        saveEndPoint: this.groupEndpoint,
        recordEndPoint: this.groupEndpoint,
        exportFileName: `${this.groupLabel} List`,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: this.groupEndpoint,
              filter: "",
              serverPaging: true,
              allowGrouping: false,
            },
            rowSelect: {
              view: this,
              page: `gl-reporting-group-${this.groupNumberPadded}`,
              mode: "review",
              idProperty: "Code",
              idMode: this.codeIsNumeric ? "Number" : undefined,
            },
          },
          toolbar: {
            search: true,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: `${this.groupRoute}/create`,
                clickAction: {},
                buttonLocation: "left",
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              dataField: "CodeComputedString",
              allowHiding: false,
            },
            {
              screenField: "Name",
              dataField: "Description",
              label: "Description",
            },
            {
              screenField: "Date",
              dataField: "DateChanged",
              label: "Date Changed",
              minWidth: ColumnWidth.Date.minWidth,
              width: ColumnWidth.Date.width,
            },
            {
              dataField: "ChangedBy",
              label: "Changed By",
              minWidth: ColumnWidth.Username.minWidth,
              width: ColumnWidth.Username.width,
            },
            { screenField: "Status", width: "auto", filterIndex: 1 },
          ],
          valueChangedEventListners: [],
        },
      };
    },
    setFormConfig() {
      return {
        headerToolbar: {
          closeSplit: true,
          save: true,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: { disabled: true },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: { disabled: true },
                },
              ],
            },
          ],
          toolbarFormItems: [
            {
              screenField: "Inactive",
              editorType: "dxSwitch",
              editorOptions: {
                readOnly: this.setActiveReadOnly,
                onValueChanged: (e) => this.onActiveValueChanged(e),
              },
              cssClass: "toolbar-customer-label",
            },
          ],
          toolbarFormLocation: "after",
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                screenField: "Code",
                dataField: "Code",
                required: true,
                allowUpdate: this.baseView.mode == "edit" ? false : true,
                cssClass: "gl-code-field",
                isPrimaryKeyField: true,
                editorOptions: {
                  maxLength: this.codeMaxLength,
                },
                validationRules: [
                  {
                    type: "custom",
                    validationCallback: (e) => {
                      if (!e.value) {
                        e.rule.message = `Code is required`;
                        return false;
                      }
                      if (this.codeIsNumeric && !/^\d+$/.test(e.value)) {
                        e.rule.message = `Code must be a whole number`;
                        return false;
                      }
                      if (e.value.length > this.codeMaxLength) {
                        e.rule.message = `Code only allow maximum length of ${this.codeMaxLength} characters`;
                        return false;
                      }
                      return true;
                    },
                  },
                ],
              },
              {
                screenField: "Name",
                label: { text: "Description" },
                required: true,
                editorOptions: {
                  maxLength: 25,
                  onFocusIn: (e) => {
                    e.event.target.select();
                  },
                },
              },
            ],
          },
        ],
        formHeight: 90,
        formContentReady: (e) => {
          // Set focus to the Code field on create mode
          if (this.baseView.mode === "create") {
            const codeField = e.component.getEditor("Code");
            if (codeField) {
              codeField.focus();
            }
          } else if (this.baseView.mode === "edit") {
            // Set focus to the Description field on edit mode
            const descriptionField = e.component.getEditor("Description");
            if (descriptionField) {
              descriptionField.focus();
            }
          }
        },
      };
    },
  },
  data() {
    return {
      validationSummary: [],
      formModel: {
        Code: "",
        Description: "",
        Inactive: true,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
        RecordId: "",
      },
      setLoading: false,
      loaded: false,
    };
  },
  methods: {
    async onActiveValueChanged(e) {
      if (
        this.baseView.mode == "edit" &&
        e.value == false &&
        e.previousValue == true
      ) {
        this.setLoading = true;
        await GLChartService.getGLChartData(
          `${this.groupEndpoint}Code eq '${this.formModel["Code"]}'`,
        )
          .then((res) => {
            this.setLoading = false;
            if (res.data.value.length > 0) {
              this.formModel.Inactive = true;
              e.component.beginUpdate();
              e.component.option("value", true);
              e.component.endUpdate();

              this.validationSummary = [
                {
                  message: `${this.formModel["Description"]} is associated with GL Account records. This may not be marked inactive until it is no longer associated with GL Account records.`,
                },
                {
                  message: `Tip: To identify the associated GL Account use the filters in the GL Accounts list.`,
                },
              ];
            }
          })
          .catch((err) => {
            if (err) this.setLoading = false;
          });
      }
    },
  },
  created() {
    this.$nextTick(() => {
      this.loaded = true;
    });
  },
};
</script>

<style lang="scss">
.gl-code-field .dx-texteditor-input-container input {
  text-align: start;
}
</style>
